import { useState } from "react";
import { ChevronDown, ChevronUp, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import {
  addPerguntaCicloApi,
  moverPerguntaCicloApi,
  removePerguntaCicloApi,
  updatePerguntaCicloApi,
  type GgCompetenciaDto,
  type GgPerguntaLiderancaDto,
} from "@/services/genteGestao";

export function AbaLiderancaPerguntas({
  cicloId,
  perguntas,
  competencias,
  bloqueado,
  onChanged,
}: {
  cicloId: string;
  perguntas: GgPerguntaLiderancaDto[];
  competencias: GgCompetenciaDto[];
  bloqueado: boolean;
  onChanged: () => void;
}) {
  const [adding, setAdding] = useState(false);
  const [novoTipo, setNovoTipo] = useState<GgPerguntaLiderancaDto["tipo"]>("ESCALA_1_5");
  const [novoTexto, setNovoTexto] = useState("");
  const [novaComp, setNovaComp] = useState("");
  const [novoPeso, setNovoPeso] = useState("1");
  const [error, setError] = useState<string | null>(null);
  const ativas = competencias.filter((c) => c.ativo);

  async function salvar(p: GgPerguntaLiderancaDto, patch: Partial<GgPerguntaLiderancaDto>) {
    await updatePerguntaCicloApi(cicloId, p.id, patch);
    onChanged();
  }

  return (
    <div className="flex flex-col gap-3">
      {bloqueado && (
        <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          O ciclo já recebeu respostas. As perguntas não podem mais ser alteradas.
        </p>
      )}
      {error && <p className="text-sm text-red-600">{error}</p>}
      {perguntas.length === 0 && (
        <p className="text-sm text-slate-500">Nenhuma pergunta. Adicione ou crie o ciclo a partir de um modelo.</p>
      )}
      <ul className="flex flex-col gap-2">
        {perguntas.map((p, idx) => (
          <li key={p.id} className="rounded-xl border border-slate-200 bg-white p-3 shadow-card">
            <div className="mb-2 flex flex-wrap items-center gap-2 text-xs text-slate-500">
              <span className="font-semibold text-slate-700">#{p.ordem}</span>
              <span>{p.tipo === "TEXTO" ? "Texto" : "Escala 1–5"}</span>
              {p.competenciaNome && <span>{p.competenciaNome}</span>}
              {p.tipo === "ESCALA_1_5" && <span>Peso {p.peso}</span>}
              <label className="ml-auto flex items-center gap-1">
                <input
                  type="checkbox"
                  checked={p.obrigatoria}
                  disabled={bloqueado}
                  onChange={(e) => void salvar(p, { obrigatoria: e.target.checked })}
                />
                Obrigatória
              </label>
            </div>
            <textarea
              className="w-full rounded-lg border border-slate-200 bg-slate-50 p-2 text-sm"
              rows={2}
              defaultValue={p.texto}
              disabled={bloqueado}
              onBlur={(e) => {
                const v = e.target.value.trim();
                if (v && v !== p.texto) void salvar(p, { texto: v });
              }}
            />
            {!bloqueado && p.tipo === "ESCALA_1_5" && (
              <div className="mt-2 grid gap-2 sm:grid-cols-2">
                <Select
                  label="Competência"
                  value={p.competenciaId ?? ""}
                  onChange={(e) => void salvar(p, { competenciaId: e.target.value || null })}
                  options={ativas.map((c) => ({ value: c.id, label: c.nome }))}
                  placeholder="Sem competência"
                />
                <Input
                  label="Peso"
                  type="number"
                  min={0.1}
                  step={0.1}
                  defaultValue={String(p.peso)}
                  onBlur={(e) => {
                    const n = Number(e.target.value);
                    if (n > 0 && n !== p.peso) void salvar(p, { peso: n });
                  }}
                />
              </div>
            )}
            {!bloqueado && (
              <div className="mt-2 flex gap-1">
                <Button size="sm" variant="ghost" disabled={idx === 0} onClick={() => void moverPerguntaCicloApi(cicloId, p.id, "up").then(onChanged)}>
                  <ChevronUp className="h-4 w-4" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={idx === perguntas.length - 1}
                  onClick={() => void moverPerguntaCicloApi(cicloId, p.id, "down").then(onChanged)}
                >
                  <ChevronDown className="h-4 w-4" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() =>
                    void removePerguntaCicloApi(cicloId, p.id)
                      .then(onChanged)
                      .catch((e) => setError((e as { response?: { data?: { error?: string } } }).response?.data?.error || "Erro"))
                  }
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            )}
          </li>
        ))}
      </ul>
      {!bloqueado && (
        adding ? (
          <div className="rounded-xl border border-slate-200 bg-white p-3">
            <Select
              label="Tipo"
              value={novoTipo}
              onChange={(e) => setNovoTipo(e.target.value as GgPerguntaLiderancaDto["tipo"])}
              options={[
                { value: "ESCALA_1_5", label: "Escala 1–5" },
                { value: "TEXTO", label: "Texto" },
              ]}
            />
            {novoTipo === "ESCALA_1_5" && (
              <div className="mt-2 grid gap-2 sm:grid-cols-2">
                <Select
                  label="Competência"
                  value={novaComp}
                  onChange={(e) => setNovaComp(e.target.value)}
                  options={ativas.map((c) => ({ value: c.id, label: c.nome }))}
                  placeholder="Opcional"
                />
                <Input label="Peso" type="number" min={0.1} step={0.1} value={novoPeso} onChange={(e) => setNovoPeso(e.target.value)} />
              </div>
            )}
            <textarea
              className="mt-2 w-full rounded-lg border border-slate-200 bg-slate-50 p-2 text-sm"
              rows={2}
              placeholder="Texto da pergunta"
              value={novoTexto}
              onChange={(e) => setNovoTexto(e.target.value)}
            />
            <div className="mt-2 flex gap-2">
              <Button
                size="sm"
                onClick={() => {
                  void addPerguntaCicloApi(cicloId, {
                    tipo: novoTipo,
                    texto: novoTexto,
                    competenciaId: novaComp || null,
                    peso: Number(novoPeso) || 1,
                  })
                    .then(() => {
                      setAdding(false);
                      setNovoTexto("");
                      onChanged();
                    })
                    .catch((e) => setError((e as { response?: { data?: { error?: string } } }).response?.data?.error || "Erro"));
                }}
              >
                Salvar
              </Button>
              <Button size="sm" variant="secondary" onClick={() => setAdding(false)}>
                Cancelar
              </Button>
            </div>
          </div>
        ) : (
          <Button size="sm" leftIcon={<Plus className="h-4 w-4" />} onClick={() => setAdding(true)}>
            Adicionar pergunta
          </Button>
        )
      )}
    </div>
  );
}
